import React, { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { v4 as uuidv4 } from 'uuid';

import { SitecoreJsonImage } from 'common/types/sitecore';
import {
  BreakpointMatches,
  isAboveBreakpoint,
  isBelowBreakpoint,
  matchesBreakpoint,
  useWatchScreenSize,
} from 'utils/hooks/useBreakpoint';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

export type SliderImage = {
  identifier: string;
  image: SitecoreJsonImage | ImageField;
};

type JabraGallerySliderItemProps = {
  index: number;
  active: boolean;
  children: React.ReactElement;
} & React.HTMLAttributes<HTMLDivElement>;

export const JabraGallerySliderItem = ({
  children,
  active,
  ...restProps
}: JabraGallerySliderItemProps) => {
  return <div {...restProps}>{children}</div>;
};

type SliderButtonProps = {
  onClick: () => void;
  index: number;
  active: boolean;
};

export const JabraGallerySliderButton = ({ onClick, active, index }: SliderButtonProps) => {
  const { t } = useFallbackTranslation();
  const [slideId, setSlideId] = useState('');

  useEffect(() => {
    setSlideId(uuidv4());
  }, []);

  const sliderDotActiveClass = active
    ? 'h-8 bg-yellow'
    : 'h-5 bg-black bg-opacity-50 hover:bg-opacity-75';
  return (
    <li id={slideId} className="flex items-center">
      <button
        className={classNames(
          'w-2.5 rounded-full transition-all duration-300 ease-linear',
          sliderDotActiveClass
        )}
        aria-controls={slideId}
        aria-label={`${t('system.GoToSlide')} ${index}`}
        onClick={onClick}
      />
    </li>
  );
};

type ItemType = {
  [key: string]: any;
  identifier: string;
};

type JabraGallerySliderProps<T extends ItemType> = {
  items: T[];
  itemComponent: React.ComponentType<{
    item: T;
    active?: boolean;
    index?: number;
    onClick?: () => void;
  }>;
  itemsAmountStep: number;
  enableContainerForGallery?: boolean;
  containerClassName?: string;
  carouselClassName?: string;
  showButtons?: BreakpointMatches;
  oneItem?: BreakpointMatches;
  loop?: BreakpointMatches;
};

export const JabraGallerySlider = <T extends ItemType>({
  items,
  itemComponent: ItemComponent,
  itemsAmountStep,
  carouselClassName,
  containerClassName,
  enableContainerForGallery = false,
  showButtons: showButtonsBreakpoint = 'max-md',
  oneItem: oneItemBreakpoint = 'max-md',
  loop: loopBreakpoint = false,
}: JabraGallerySliderProps<T>) => {
  const screenSize = useWatchScreenSize();

  const showButtons = matchesBreakpoint(showButtonsBreakpoint, screenSize);
  const showOneItem = matchesBreakpoint(oneItemBreakpoint, screenSize);
  const showLoop = matchesBreakpoint(loopBreakpoint, screenSize);

  const isBelowLg = isBelowBreakpoint(screenSize, 'lg');
  const isAboveLg = isAboveBreakpoint(screenSize, 'lg');
  const isAbove2xl = isAboveBreakpoint(screenSize, '2xl');

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: showOneItem ? 'center' : 'start',
    containScroll: showOneItem && !showLoop ? false : 'trimSnaps',
    loop: showLoop,
  });

  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [selectedSnapIndex, setSelectedSnapIndex] = useState(0);

  const itemsLessThanMaxAmountInRow = useMemo(() => {
    if (isAbove2xl) {
      return items.length <= 3 * itemsAmountStep;
    } else if (isAboveLg) {
      return items.length <= 2 * itemsAmountStep;
    } else if (isBelowLg) {
      return items.length <= itemsAmountStep;
    }

    return false;
  }, [isAbove2xl, isAboveLg, isBelowLg, items.length, itemsAmountStep]);

  const handleButtonClick = (index: number) => {
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollTo(index);
    setSelectedIndex(index);
    setActiveItemIndex(index);
  };

  const handleItemClick = (index: number) => {
    setActiveItemIndex(index);

    if (showOneItem) {
      setSelectedIndex(index);
      emblaApi?.scrollTo(index);
    }
  };

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      const snapIndex = emblaApi.selectedScrollSnap();

      setSelectedIndex(snapIndex);

      if (showOneItem) {
        setActiveItemIndex(snapIndex);
      }
    };

    onSelect();
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onSelect);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, showOneItem]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      emblaApi?.scrollPrev();
      setSelectedIndex(selectedIndex - 1);

      if (!showOneItem && activeItemIndex) {
        setActiveItemIndex(activeItemIndex - 1);
      }
    } else if (event.key === 'ArrowRight') {
      emblaApi?.scrollNext();

      setSelectedIndex(selectedIndex + 1);

      if (!showOneItem && activeItemIndex < items.length - 1) {
        setActiveItemIndex(activeItemIndex + 1);
      }
    }
  };
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedSnapIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  const hasMoreThanOneSlide = scrollSnaps.length > 1;
  return (
    <div className="outline-none" onKeyDown={handleKeyDown} tabIndex={-1}>
      <div
        className={classNames('w-full overflow-hidden', carouselClassName, {
          container: enableContainerForGallery,
        })}
        ref={emblaRef}
      >
        <div
          className={classNames(
            'flex touch-pan-y',
            containerClassName,
            itemsLessThanMaxAmountInRow && 'justify-center'
          )}
        >
          {items.map((item, index) => (
            <ItemComponent
              item={item}
              key={item.identifier}
              index={index}
              active={activeItemIndex === index}
              onClick={() => handleItemClick(index)}
            />
          ))}
        </div>
      </div>
      <ul
        className={classNames(
          'hidden h-[38px] items-center justify-center gap-2 pt-2',
          showButtons && hasMoreThanOneSlide && '!flex'
        )}
        role="tablist"
      >
        {hasMoreThanOneSlide &&
          scrollSnaps.map((snap, index) => {
            return (
              <JabraGallerySliderButton
                key={`button-${snap}`}
                onClick={() => handleButtonClick(index)}
                active={selectedSnapIndex === index}
                index={index}
              />
            );
          })}
      </ul>
    </div>
  );
};
